import { istBelegt } from '../tragezeit';
import type { AsplanBundle, AsplanUnternehmen, Tragezeit } from '../types';

/**
 * Welche Formulare dieser Plan hergibt — und was ihnen zur Vollstaendigkeit
 * fehlt.
 *
 * Bewusst NEBEN den Komponenten, aus demselben Grund wie beim
 * `aushangKatalog`: eine Datei, die Komponenten UND Funktionen ausfuehrt,
 * verliert im Entwicklungsbetrieb das Fast Refresh, und der Lint dieses
 * Projekts laeuft mit `--max-warnings 0`. Der zweite Grund wiegt schwerer:
 * ohne jsdom ist im Projekt nur pruefbar, was NICHT in einer Komponente
 * steht. Alles, was hier eine Aussage trifft — „dieser Bogen ist
 * verfuegbar", „diese Angabe fehlt", „dieser Sollwert darf gedruckt werden" —
 * ist deshalb eine reine Funktion und hat einen Test.
 *
 * ═══ UNTERSCHIED ZUM AUSHANG ══════════════════════════════════════════════
 * Ein Aushang haengt und wird gelesen. Ein Formular wird AUSGEFUELLT und
 * unterschrieben, und es wird zur Beweisurkunde: das Schleusenbuch beweist,
 * wer wie lange im Schwarzbereich war, der Tragezeitnachweis, dass die
 * Begrenzung eingehalten wurde, die Verpflichtungserklaerung, dass der
 * Auftragnehmer den Plan kannte. Daraus folgen zwei Regeln:
 *
 *   1. Was der Plan weiss, steht vorgedruckt drauf — sonst schreibt es jemand
 *      auf der Baustelle aus dem Kopf falsch ab.
 *   2. Was der Plan NICHT weiss, wird sichtbar als offen ausgewiesen und
 *      nicht stillschweigend als Leerzeile getarnt. Eine Leerzeile sieht auf
 *      dem Papier aus wie „ist auszufuellen“; eine fehlende Angabe des Plans
 *      ist aber ein Mangel des Plans und keine Aufgabe des Ausfuellenden.
 */

// ═══════════════════════════════════════════════════════════════════════════
// EINTRAEGE
// ═══════════════════════════════════════════════════════════════════════════

export interface FormularEintrag {
    key: string;
    titel: string;
    /** Wer den Bogen fuehrt und wohin er danach geht. */
    ort: string;
    /** Was fehlt, damit der Vordruck vollstaendig vorbelegt ist. */
    fehlt?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// KENNTNISNAHME- UND VERPFLICHTUNGSERKLAERUNG
// ═══════════════════════════════════════════════════════════════════════════

export interface VerpflichtungsPunkt {
    nr: number;
    kopf: string;
    text: string;
}

/**
 * Die acht Punkte der Erklaerung.
 *
 * Sie sind BEWUSST allgemein gehalten und nennen keine Schutzklasse, keine
 * Feldnummer und keinen Firmennamen: die Erklaerung wird in jedem Projekt
 * ausgegeben, und ein projektbezogener Satz an dieser Stelle wanderte
 * ungeprueft in den naechsten Plan — derselbe Grundsatz wie bei den
 * Textbausteinen.
 */
export const VERPFLICHTUNG_PUNKTE: VerpflichtungsPunkt[] = [
    {
        nr: 1,
        kopf: 'Der Arbeits- und Sicherheitsplan ist bekannt',
        text: 'Der Auftragnehmer hat den Arbeits- und Sicherheitsplan in der oben genannten '
            + 'Fassung erhalten, gelesen und verstanden. Er erkennt ihn als verbindliche '
            + 'Grundlage seiner Arbeiten an.',
    },
    {
        nr: 2,
        kopf: 'Die Beschäftigten werden vorher unterwiesen',
        text: 'Alle eingesetzten Beschäftigten werden vor Aufnahme der Arbeiten anhand des '
            + 'Plans und der tätigkeitsbezogenen Betriebsanweisung unterwiesen. Die '
            + 'Unterweisung wird schriftlich nachgewiesen und mindestens jährlich '
            + 'wiederholt.',
    },
    {
        nr: 3,
        kopf: 'Ein sachkundiger Aufsichtführender ist benannt',
        text: 'Der Auftragnehmer benennt einen Aufsichtführenden mit der Sachkunde nach '
            + 'DGUV Regel 101-004 Anhang 6A. Dieser ist während der Arbeiten in '
            + 'kontaminierten Bereichen ständig auf der Baustelle anwesend.',
    },
    {
        nr: 4,
        kopf: 'Die arbeitsmedizinische Vorsorge ist veranlasst',
        text: 'Ein betreuender Arbeitsmediziner ist benannt. Die erforderliche Vorsorge ist '
            + 'vor dem ersten Einsatz durchgeführt; ohne sie wird niemand in einem '
            + 'kontaminierten Bereich eingesetzt.',
    },
    {
        nr: 5,
        kopf: 'Die Schutzausrüstung wird gestellt und ihre Benutzung überwacht',
        text: 'Die im Plan für den jeweiligen Arbeitsbereich festgelegte persönliche '
            + 'Schutzausrüstung wird vollständig bereitgestellt, geprüft und ersetzt. Die '
            + 'Tragezeitbegrenzungen für Atemschutz werden eingehalten und nachgewiesen.',
    },
    {
        nr: 6,
        kopf: 'Die Arbeiten werden der Berufsgenossenschaft angezeigt',
        text: 'Der Auftragnehmer zeigt die Arbeiten spätestens vier Wochen vor Beginn bei '
            + 'der für ihn zuständigen Berufsgenossenschaft an (Abschnitt 11.2, Vordruck '
            + 'nach Anhang 1). Eine Kopie der Anzeige legt er dem Koordinator vor.',
    },
    {
        nr: 7,
        kopf: 'Nachunternehmer werden ebenso verpflichtet',
        text: 'Der Auftragnehmer gibt den Plan an alle von ihm eingesetzten Nachunternehmer '
            + 'weiter und lässt sich diese Erklärung von ihnen ebenfalls unterschreiben. '
            + 'Die unterschriebenen Erklärungen legt er dem Koordinator vor.',
    },
    {
        nr: 8,
        kopf: 'Abweichungen werden unverzüglich gemeldet',
        text: 'Unerwartete Funde, Geruch, Verfärbungen, geänderte Arbeitsverfahren und '
            + 'jede Abweichung vom Plan werden dem Koordinator unverzüglich angezeigt. '
            + 'Eine Schutzklasse wird niemals ohne dessen schriftliche Bestätigung '
            + 'abgesenkt.',
    },
];

/**
 * Welche Angaben zum Auftragnehmer der Vordruck noch nicht vorbelegen kann.
 *
 * Rueckgabe in Lesereihenfolge des Formulars, damit der Hinweis in der Liste
 * und die Luecke auf dem Blatt dieselbe Reihenfolge haben.
 */
export const fehlendeAngabenVerpflichtung = (u: AsplanUnternehmen): string[] => {
    const fehlt: string[] = [];
    if (!u.unternehmen.trim()) fehlt.push('Name des Unternehmens');
    if (!u.anschrift.trim()) fehlt.push('Anschrift');
    if (!u.gewerk.trim()) fehlt.push('Gewerk');
    if (!u.aufsichtsfuehrender.trim()) fehlt.push('Aufsichtführender');
    if (!u.aufsicht_telefon.trim()) fehlt.push('Telefon des Aufsichtführenden');
    if (!u.sachkunde_vorgelegt) fehlt.push('Sachkundenachweis nach Anhang 6A');
    if (u.arbeitsmediziner_status !== 'benannt') fehlt.push('betreuender Arbeitsmediziner');
    return fehlt;
};

// ═══════════════════════════════════════════════════════════════════════════
// ANZEIGE NACH ABSCHNITT 11.2 (ANHANG 1)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die zustaendige Berufsgenossenschaft dieses Unternehmens.
 *
 * Auf einer Baustelle koennen mehrere zustaendig sein — die Anzeige geht an
 * die BG des ANZEIGENDEN Unternehmens, nicht an „die BG der Baustelle".
 * Deshalb wird ueber `gilt_fuer` zugeordnet und nicht einfach die erste
 * genommen.
 */
export const bgDesUnternehmens = (b: AsplanBundle, u: AsplanUnternehmen): string => {
    const treffer = b.beteiligte.find(
        p => p.funktion_key === 'bg' && p.gilt_fuer.includes(u.unternehmen));
    return treffer?.organisation ?? '';
};

/** Was die Anzeige aus dem Plan noch nicht vorbelegen kann. */
export const fehlendeAngabenAnzeige = (b: AsplanBundle, u: AsplanUnternehmen): string[] => {
    const fehlt: string[] = [];
    if (!bgDesUnternehmens(b, u)) fehlt.push('zuständige Berufsgenossenschaft');
    if (b.plan.stoffe.filter(s => s.aktiv).length === 0) fehlt.push('Liste der Gefahrstoffe');
    if (b.cluster.every(c => !c.baumassnahme.trim())) fehlt.push('Beschreibung der Baumaßnahme');
    if (u.anzahl_ma === null) fehlt.push('Zahl der Beschäftigten');
    if (!u.aufsichtsfuehrender.trim()) fehlt.push('Aufsichtführender');
    return fehlt;
};

// ═══════════════════════════════════════════════════════════════════════════
// ATEMSCHUTZ: GERAETE, FILTER, SOLLWERTE
// ═══════════════════════════════════════════════════════════════════════════

export interface AtemschutzZeile {
    /** Bezeichnung des Geraets, wie sie in der Schutzklasse steht. */
    geraet: string;
    filtertyp: string;
    /** Die Schutzklassen, die dieses Geraet verlangen. */
    klassen: string[];
    /** Der belegte Sollwert — oder `null`, wenn keiner gedruckt werden darf. */
    soll: TragezeitSoll | null;
}

export interface TragezeitSoll {
    einsatz_min: number;
    erholung_min: number | null;
    quelle: string;
}

/**
 * Der Sollwert, der auf den Nachweisbogen gedruckt werden darf.
 *
 * `null` heisst „nichts drucken". Das ist der Kern dieses Formulars: In
 * `tragezeit.ts` tragen alle Geraetegruppen ausser der Vollmaske
 * `beleg: 'zu_pruefen'` und rechnen bewusst nicht. Ein Bogen, der trotzdem
 * eine Minutenzahl vordruckt, saehe aus wie die Regel und wuerde als solche
 * gelesen — auf einem Blatt, das unterschrieben wird und den Nachweis der
 * Einhaltung fuehren soll, ist das die gefaehrlichste Art von Erfindung.
 *
 * Geprueft wird deshalb DOPPELT: der Wert muss an der Schutzklasse
 * gespeichert sein UND seine Geraetegruppe muss belegt sein. Der zweite
 * Haken faengt Altbestaende ab, die vor einer Katalogaenderung gespeichert
 * wurden.
 */
export const tragezeitSoll = (t?: Tragezeit): TragezeitSoll | null => {
    if (!t || !t.ergebnis_min || !t.quelle?.trim()) return null;
    if (t.geraetegruppe && !istBelegt(t.geraetegruppe)) return null;
    return {
        einsatz_min: t.ergebnis_min,
        erholung_min: t.erholung_min ?? null,
        quelle: t.quelle,
    };
};

/**
 * Der Sollwert als Schluessel — genau die Angaben, die auf dem Bogen stehen.
 *
 * Die Quelle gehoert dazu: sie wird mitgedruckt, und zwei gleiche Minuten-
 * zahlen aus zwei verschiedenen Herleitungen sind auf einem Nachweisbogen
 * zwei Aussagen.
 */
const sollSchluessel = (s: TragezeitSoll | null): string =>
    s === null ? 'ohne' : `${s.einsatz_min}|${s.erholung_min ?? ''}|${s.quelle}`;

/**
 * Die Atemschutzgeraete dieses Plans, je Geraet, Filtertyp UND Sollwert
 * einmal.
 *
 * Zusammengefasst wird ueber Geraet und Filtertyp: dieselbe Vollmaske mit
 * A2P3 und mit Hg-P3 sind zwei Zeilen im Filterbuch, weil die Standzeit eine
 * andere ist.
 *
 * ═══ WARUM DER SOLLWERT MIT IN DEN SCHLUESSEL GEHOERT ═════════════════════
 * Die zulaessige Tragedauer haengt nicht am Geraet allein, sondern an den
 * Bedingungen der Schutzklasse: dieselbe Vollmaske A2-P3 darf unter SK2
 * 120 Minuten getragen werden und unter SK3 — mit Hitze und Chemikalien-
 * schutzanzug — nur 65.
 *
 * Bis hierher gewann der zuerst gefundene Wert, und weil aufsteigend nach
 * Rang gelesen wird, war das der Wert der NIEDRIGEREN Klasse. Auf dem
 * unterschriebenen Nachweisbogen stand dann fuer den SK3-Traeger fast das
 * Doppelte des Zulaessigen.
 *
 * Nicht zusammenfassen statt das Minimum bilden: das Minimum waere zwar nie
 * gefaehrlich, aber fuer die andere Klasse falsch — und der Bogen sagt nicht,
 * welche Klasse gemeint ist. Zwei Zeilen sagen beides richtig. Aus demselben
 * Grund erbt eine Klasse OHNE belegten Sollwert keinen fremden mehr: sie
 * bekommt ihre eigene Zeile mit dem Vermerk, dass der Wert fehlt.
 */
export const atemschutzGeraete = (b: AsplanBundle): AtemschutzZeile[] => {
    const nachSchluessel = new Map<string, AtemschutzZeile>();
    for (const k of [...b.schutzklassen].sort((x, y) => x.rang - y.rang)) {
        const geraet = (k.atemschutz.geraet ?? '').trim();
        if (!geraet) continue;
        const filtertyp = (k.atemschutz.filtertyp ?? '').trim();
        const soll = tragezeitSoll(k.tragezeit);
        const schluessel = `${geraet}|${filtertyp}|${sollSchluessel(soll)}`;
        const vorhanden = nachSchluessel.get(schluessel);
        if (vorhanden) {
            vorhanden.klassen.push(k.key);
            continue;
        }
        nachSchluessel.set(schluessel, { geraet, filtertyp, klassen: [k.key], soll });
    }
    return [...nachSchluessel.values()];
};

/**
 * Die Zeilen, die im Filterbuch vorbelegt werden koennen — nur Geraete mit
 * einem festgelegten Filtertyp. Ein Filterbuch ohne Filtertyp fuehrt zu
 * Eintraegen wie „Filter gewechselt", die nichts belegen.
 *
 * Je Geraet und Filtertyp genau eine Zeile: das Filterbuch protokolliert
 * Einbau und Wechsel und kennt den Sollwert der Tragezeit gar nicht. Die
 * Aufteilung nach Sollwert aus `atemschutzGeraete` wird hier deshalb wieder
 * zusammengezogen — sonst stuende dieselbe Geraet-Filter-Kombination zweimal
 * im Bogen.
 */
export const filterZeilen = (b: AsplanBundle): AtemschutzZeile[] => {
    const gesehen = new Set<string>();
    return atemschutzGeraete(b)
        .filter(z => !!z.filtertyp)
        .filter(z => {
            const s = `${z.geraet}|${z.filtertyp}`;
            if (gesehen.has(s)) return false;
            gesehen.add(s);
            return true;
        });
};

// ═══════════════════════════════════════════════════════════════════════════
// DIE LISTE
// ═══════════════════════════════════════════════════════════════════════════

const AUSGABE_KOORDINATOR = 'ausgeben und unterschrieben an den Koordinator zurück';

export const verfuegbareFormulare = (b: AsplanBundle): FormularEintrag[] => {
    const liste: FormularEintrag[] = [];

    // ── Verpflichtungserklaerung: je Auftragnehmer eine ────────────────────
    if (b.unternehmen.length === 0) {
        liste.push({
            key: 'verpflichtung',
            titel: 'Kenntnisnahme- und Verpflichtungserklärung',
            ort: AUSGABE_KOORDINATOR,
            fehlt: 'Es ist noch kein Auftragnehmer erfasst, der Vordruck bleibt ohne '
                + 'Angaben zum Unternehmen.',
        });
    } else {
        for (const u of b.unternehmen) {
            const offen = fehlendeAngabenVerpflichtung(u);
            liste.push({
                key: `verpflichtung_${u.id}`,
                titel: `Verpflichtungserklärung: ${u.unternehmen || 'Unternehmen ohne Namen'}`,
                ort: AUSGABE_KOORDINATOR,
                fehlt: offen.length > 0 ? `Noch offen: ${offen.join(', ')}.` : undefined,
            });
        }
    }

    // ── Anzeige nach Abschnitt 11.2: ebenfalls je Auftragnehmer ────────────
    if (b.unternehmen.length === 0) {
        liste.push({
            key: 'anzeige',
            titel: 'Anzeige nach Abschnitt 11.2 (Anhang 1)',
            ort: 'vom Auftragnehmer an seine Berufsgenossenschaft, Kopie an den Koordinator',
            fehlt: 'Es ist noch kein Auftragnehmer erfasst. Ohne ihn steht weder der '
                + 'Anzeigende noch die zuständige Berufsgenossenschaft fest.',
        });
    } else {
        for (const u of b.unternehmen) {
            const offen = fehlendeAngabenAnzeige(b, u);
            liste.push({
                key: `anzeige_${u.id}`,
                titel: `Anzeige nach Abschnitt 11.2: ${u.unternehmen || 'Unternehmen ohne Namen'}`,
                ort: 'vom Auftragnehmer an seine Berufsgenossenschaft, Kopie an den Koordinator',
                fehlt: offen.length > 0
                    ? `Der Auftragnehmer muss selbst ergänzen: ${offen.join(', ')}.`
                    : undefined,
            });
        }
    }

    // ── Schleusenbuch ──────────────────────────────────────────────────────
    const bereiche = b.cluster.filter(c => c.art !== 'abgegrenzt');
    liste.push({
        key: 'schleusenbuch',
        titel: 'Schleusenbuch',
        ort: 'in der Schwarz-Weiß-Anlage, weiße Seite; ausgefüllt zum Aufsichtführenden',
        fehlt: bereiche.length === 0
            ? 'Es ist noch kein Arbeitsbereich erfasst, die Spalte „Bereich“ hat keine Auswahl.'
            : bereiche.some(c => !c.schutzklasse_key)
                ? 'Mindestens einem Arbeitsbereich ist keine Schutzklasse zugeordnet, '
                    + 'die Spalte „Schutzklasse“ lässt sich dort nicht vorbelegen.'
                : undefined,
    });

    // ── Filterbuch ─────────────────────────────────────────────────────────
    const filter = filterZeilen(b);
    liste.push({
        key: 'filterbuch',
        titel: 'Filterbuch',
        ort: 'bei der Atemschutzausgabe; verbleibt beim Auftragnehmer',
        fehlt: filter.length === 0
            ? 'Keine Schutzklasse dieses Plans nennt einen Filtertyp, der Bogen wird '
                + 'dann ohne Vorbelegung gedruckt.'
            : undefined,
    });

    // ── Tragezeitnachweis ──────────────────────────────────────────────────
    const geraete = atemschutzGeraete(b);
    const ohneSoll = geraete.filter(z => !z.soll);
    liste.push({
        key: 'tragezeit',
        titel: 'Tragezeitnachweis Atemschutz',
        ort: 'beim Aufsichtführenden, je Person und Schicht',
        fehlt: geraete.length === 0
            ? 'Keine Schutzklasse dieses Plans nennt ein Atemschutzgerät.'
            : ohneSoll.length === geraete.length
                ? 'Für kein Gerät dieses Plans liegt ein belegter Grenzwert vor, der '
                    + 'Bogen druckt deshalb keinen Sollwert. Werte aus DGUV Regel 112-190 '
                    + 'Anhang 2 an der Schutzklasse hinterlegen.'
                : ohneSoll.length > 0
                    // Mit der Schutzklasse: dasselbe Geraet kann in einer
                    // Klasse einen belegten Wert haben und in der naechsten
                    // nicht — „Vollmaske" allein waere dann nicht auffindbar.
                    ? 'Ohne belegten Grenzwert: '
                        + `${ohneSoll.map(z => `${z.geraet} (${z.klassen.join(', ')})`).join(', ')}.`
                    : undefined,
    });

    // ── Heissarbeitserlaubnis ──────────────────────────────────────────────
    liste.push({
        key: 'heissarbeit',
        titel: 'Erlaubnisschein für Heißarbeiten',
        ort: 'Bauleitung; ein Durchschlag bleibt bei der Brandwache',
        fehlt: b.plan.messkonzept.some(z => z.messziel === 'akut')
            ? undefined
            : 'Das Messkonzept nennt kein Messziel „akute Gefahren“, die Freimessung vor '
                + 'Heißarbeiten ist damit nicht geregelt.',
    });

    // ── Unterweisungsnachweis ──────────────────────────────────────────────
    liste.push({
        key: 'unterweisung',
        titel: 'Unterweisungsnachweis',
        ort: 'beim Aufsichtführenden des Auftragnehmers, Kopie an den Koordinator',
    });

    // ── Uebergabe / Abnahme ────────────────────────────────────────────────
    liste.push({
        key: 'uebergabe',
        titel: 'Übergabeprotokoll / Abnahme',
        ort: 'Koordinator und Auftraggeber, je eine Ausfertigung',
        fehlt: bereiche.length === 0
            ? 'Es ist noch kein Arbeitsbereich erfasst, das Protokoll listet dann keinen '
                + 'Bereich zur Abnahme.'
            : undefined,
    });

    return liste;
};
